/**
 * Contrato del Sorteo Lotowheel — SSOT canónico. Alineado con el patrón
 * aplicado en pozos y nodos:
 *
 *   - Campos planos con paths con puntos.
 *   - Cada apuesta tiene `aplicaSi(config)` que delega al SSOT existente
 *     `roulette-numbers-order-config.js > availableBets` — cero matemática
 *     duplicada. Si el SSOT cambia (ej. soportar mini24), este contrato
 *     se ajusta automáticamente.
 *   - Defaults importados de `defaults.js` (ya SSOT).
 *
 * Estrategia: el frontend del modal admin crea un Borrador del snapshot
 * crudo, llama `hidratarConDefaultsDelContrato`, y arranca con un
 * snapshot completo y sano. Si el usuario abre y guarda sin tocar nada,
 * el toast canónico devuelve "sin cambios" honesto.
 *
 * Para los 21 sorteos en producción con basura histórica (limites null,
 * resolver fósil): la hidratación al primer GET los autoreparra.
 */

import {
    ROULETTE_TYPES,
    ZERO_DISPLAY_MODES,
    normalizarClaveDeTipoDeRueda,
} from '../config/games/lotowheel/roulette-numbers-order-config.js';
import { IDIOMAS_SOPORTADOS } from '../config/api-nomenclatura.js';
import {
    DEFAULT_CONFIG,
    DEFAULT_LIMITES,
    DEFAULT_FICHAS,
    DEFAULT_SPIN,
    DEFAULT_EFFECTS,
    DEFAULT_IDLE,
    DEFAULT_TIMING,
} from '../config/games/lotowheel/defaults.js';
import { MODALIDAD_LOTOWHEEL, MODALIDAD_LOTOWHEEL_POR_DEFECTO } from '../config/games/lotowheel/modalidad-lotowheel.js';

// ───────────────────────────────────────────────────────────────────────
// Helper: ¿esta apuesta aplica para este tipoRuleta?
//
// Delega al SSOT `roulette-numbers-order-config.js > availableBets`. Las
// apuestas no listadas en availableBets (pleno, color, paridad, mitad,
// tercios, columnas, bloques) están disponibles en TODOS los tipos.
// ───────────────────────────────────────────────────────────────────────
function _resolverAvailableBetsParaTipoRuleta(tipoRuleta) {
    // Fail-fast: este helper resuelve QUE apuestas son validas para el sorteo.
    // Si tipoRuleta llega vacio y caemos a european, validariamos apuestas
    // contra una rueda que NO es la del sorteo (ej. aceptar paridad en una
    // rueda10 que no la tiene) — bug silencioso de money-path.
    if (!tipoRuleta) {
        throw new Error(
            '[CONTRATO sorteo-lotowheel] tipoRuleta es obligatorio para resolver apuestas disponibles. ' +
                'Caller bug: config sin tipoRuleta no debe llegar al validador.'
        );
    }
    // SSOT de normalización: `normalizarClaveDeTipoDeRueda` (caja canónica
    // lowercase). Una sola función para que la convención no se replique inline.
    const tipo = normalizarClaveDeTipoDeRueda(tipoRuleta);
    const cfg = ROULETTE_TYPES[tipo];
    if (!cfg) {
        throw new Error(
            `[CONTRATO sorteo-lotowheel] tipoRuleta desconocido: "${tipoRuleta}". ` +
                `Validos: ${Object.keys(ROULETTE_TYPES).join(', ')}.`
        );
    }
    return cfg.availableBets || {};
}

function _aplicaSiTipoRuletaSoporta(idApuesta) {
    return config => {
        const av = _resolverAvailableBetsParaTipoRuleta(config.tipoRuleta);
        // Si la apuesta no figura en availableBets, está disponible siempre.
        if (!(idApuesta in av)) return true;
        return av[idApuesta] === true;
    };
}

// El override lightning SOLO aplica en ruedas que declaran mecánica lightning
// (Mega3000/Red Door). Una rueda normal que reciba un override lightning → el
// contrato no lo valida y el draw-engine lo ignora (obtenerLightningDeReceta corta
// en `!receta.lightning`). Lee de ROULETTE_TYPES (espejo `tieneLightning` de la receta).
function _aplicaSiRuedaTieneLightning(config) {
    if (!config.tipoRuleta) return false;
    const tipo = ROULETTE_TYPES[normalizarClaveDeTipoDeRueda(config.tipoRuleta)];
    return !!(tipo && tipo.tieneLightning);
}

// ───────────────────────────────────────────────────────────────────────
// CONTRATO
// ───────────────────────────────────────────────────────────────────────

const CONTRATO_SORTEO_LOTOWHEEL = {
    nombreEntidad: 'sorteo-lotowheel',

    campos: {
        // ─── Identidad ──────────────────────────────────────────────────
        nombre: {
            tipo: 'string',
            default: '',
            maxLength: 100,
            descripcion: 'Nombre visible del sorteo. Obligatorio.',
        },
        intervalo_segundos: {
            tipo: 'entero',
            default: 60,
            minInclusive: 3,
            maxInclusive: 86400,
            nullable: false,
            descripcion: 'Intervalo entre sorteos consecutivos en segundos. Money-path: scheduler usa este valor.',
        },
        activo: {
            tipo: 'entero',
            default: 1,
            minInclusive: 0,
            maxInclusive: 1,
            nullable: false,
            descripcion: 'Soft-delete: 0 = inactivo, 1 = activo.',
        },
        modalidad: {
            tipo: 'enum',
            opciones: Object.values(MODALIDAD_LOTOWHEEL),
            default: MODALIDAD_LOTOWHEEL_POR_DEFECTO,
            nullable: false,
            descripcion: 'Transportes habilitados: sorteo temporizado, draw individual o ambos.',
        },

        // ─── Tipo de rueda ──────────────────────────────────────────────
        // Las opciones se DERIVAN de `ROULETTE_TYPES` (no se duplica la lista).
        // `ROULETTE_TYPES` a su vez se DERIVA de `RECETAS_DE_RUEDA` — agregar
        // una receta nueva (`recetas-de-rueda.js`) aparece automáticamente en
        // `ROULETTE_TYPES` y de ahí acá. Cero código para sumar una rueda.
        //
        // Caja: las claves de `ROULETTE_TYPES` son lowercase (id de la receta).
        // Es la caja canónica del sistema — wire-format, DB (`tipo_ruleta`),
        // endpoint `wheel-data`, factory, resolver. No hace falta `.toLowerCase()`.
        tipoRuleta: {
            tipo: 'enum',
            opciones: Object.keys(ROULETTE_TYPES),
            default: DEFAULT_CONFIG.tipoRuleta,
            nullable: false,
            descripcion:
                'Tipo de ruleta. Define maxNum, ceros y apuestas disponibles vía SSOT roulette-numbers-order-config. Money-path.',
        },

        // ─── Visual ─────────────────────────────────────────────────────
        tema: {
            tipo: 'string',
            default: DEFAULT_CONFIG.tema,
            maxLength: 64,
            descripcion: 'Tema visual del front (CASINO_ROYALE, HALLOWEEN, etc). Registrado dinámicamente.',
        },
        sound_pack: {
            tipo: 'string',
            default: DEFAULT_CONFIG.sound_pack,
            maxLength: 64,
            descripcion: 'Pack de sonidos. Registrado dinámicamente.',
        },
        // Opciones derivadas del SSOT `IDIOMAS_SOPORTADOS.LISTA` (en
        // `api-nomenclatura.js`). Agregar un idioma nuevo allí lo expone en
        // el contrato automáticamente. Cero duplicación.
        idioma: {
            tipo: 'enum',
            opciones: IDIOMAS_SOPORTADOS.LISTA,
            default: DEFAULT_CONFIG.idioma,
            descripcion: 'Idioma del sorteo.',
        },

        // ─── Timing del ciclo ───────────────────────────────────────────
        close_before: {
            tipo: 'entero',
            default: DEFAULT_TIMING.close_before,
            minInclusive: 0,
            maxInclusive: 60,
            descripcion: 'Segundos antes del sorteo en que se cierran apuestas.',
        },
        warning_time: {
            tipo: 'entero',
            default: DEFAULT_TIMING.warning_time,
            minInclusive: 0,
            maxInclusive: 120,
            descripcion: 'Segundos antes del cierre para mostrar warning al jugador.',
        },
        min_usable_time: {
            tipo: 'entero',
            default: DEFAULT_TIMING.min_usable_time,
            minInclusive: 1,
            maxInclusive: 300,
            descripcion: 'Segundos mínimos útiles para que el jugador apueste.',
        },
        spin_time: {
            tipo: 'entero',
            default: DEFAULT_TIMING.spin_time,
            minInclusive: 1,
            maxInclusive: 60,
            descripcion: 'Duración de la animación del sorteo.',
        },

        // ─── Moneda ─────────────────────────────────────────────────────
        'moneda.codigo': {
            tipo: 'string',
            default: DEFAULT_CONFIG.moneda.codigo,
            nullable: false,
            maxLength: 16,
            descripcion:
                'Código de moneda. Money-path. Acepta ISO 4217 (PEN, BRL, USD, EUR, 3 chars) y tickers crypto/stablecoin multi-chain (USDT, USDC.e, hasta 16 chars) — soporte crypto planeado próximo trimestre.',
        },
        'moneda.simbolo': {
            tipo: 'string',
            default: DEFAULT_CONFIG.moneda.simbolo,
            maxLength: 8,
            descripcion: 'Símbolo visual de la moneda.',
        },
        'moneda.decimales': {
            tipo: 'entero',
            default: DEFAULT_CONFIG.moneda.decimales,
            minInclusive: 0,
            maxInclusive: 8,
            nullable: false,
            descripcion: 'Cantidad de decimales que se muestran. Money-path: NULL produce NaN al formatear.',
        },
        'moneda.separadorDecimal': {
            tipo: 'string',
            default: DEFAULT_CONFIG.moneda.separadorDecimal,
            maxLength: 2,
            descripcion: 'Separador decimal (. o ,).',
        },
        'moneda.separadorMiles': {
            tipo: 'string',
            default: DEFAULT_CONFIG.moneda.separadorMiles,
            maxLength: 2,
            descripcion: 'Separador de miles (, o .).',
        },

        // ─── Fichas ─────────────────────────────────────────────────────
        'fichas.denominacion': {
            tipo: 'entero',
            default: DEFAULT_FICHAS.denominacion,
            minInclusive: 1,
            maxInclusive: 1_000_000,
            nullable: false,
            descripcion: 'Centavos por crédito. 100 = 1 crédito por unidad de moneda. Money-path crítico.',
        },
        // chips es array de {valor, label, color} — lo declaramos como objeto
        // opaco, el modal arma el array entero como bloque.
        'fichas.chips': {
            tipo: 'array',
            default: DEFAULT_FICHAS.chips,
            descripcion: 'Array de fichas disponibles ({valor, label, color}).',
        },

        // ─── Límites del juego (money-path crítico) ─────────────────────
        'limites.maxApuesta': {
            tipo: 'entero',
            default: DEFAULT_LIMITES.maxApuesta,
            minInclusive: 100,
            maxInclusive: 100_000_000,
            nullable: false,
            descripcion: 'Apuesta máxima individual en centavos. Money-path: bug histórico que motivó el patrón.',
        },
        'limites.maxTotalTicket': {
            tipo: 'entero',
            default: DEFAULT_LIMITES.maxTotalTicket,
            minInclusive: 100,
            maxInclusive: 1_000_000_000,
            nullable: false,
            descripcion: 'Suma máxima de todas las apuestas de un ticket. Money-path.',
        },
        'limites.maxDrawCount': {
            tipo: 'entero',
            default: DEFAULT_LIMITES.maxDrawCount,
            minInclusive: 1,
            maxInclusive: 100,
            nullable: false,
            descripcion: 'Cantidad máxima de sorteos consecutivos por ticket.',
        },
        'limites.premioMaximo': {
            tipo: 'entero',
            default: DEFAULT_LIMITES.premioMaximo,
            minInclusive: 100,
            maxInclusive: 10_000_000_000,
            nullable: false,
            descripcion: 'Tope absoluto de premio por apuesta. Money-path.',
        },
        'limites.plenoMultiplier': {
            tipo: 'entero',
            default: DEFAULT_LIMITES.plenoMultiplier,
            minInclusive: 1,
            maxInclusive: 1000,
            nullable: false,
            descripcion: 'Multiplicador de pago para apuesta pleno (default 36). Money-path.',
        },

        // ─── Spin (animación de rueda) ──────────────────────────────────
        'spin.duration': {
            tipo: 'entero',
            default: DEFAULT_SPIN.duration,
            minInclusive: 1000,
            maxInclusive: 60000,
            descripcion: 'Duración de la animación del giro en ms.',
        },
        'spin.fmQuesitos': {
            tipo: 'entero',
            default: DEFAULT_SPIN.fmQuesitos,
            minInclusive: 1,
            maxInclusive: 100,
            descripcion: 'Cantidad de "quesitos" para FM (frequency modulation) del giro.',
        },
        'spin.cruiseRPM': {
            tipo: 'entero',
            default: DEFAULT_SPIN.cruiseRPM,
            minInclusive: 1,
            maxInclusive: 200,
            descripcion: 'Revoluciones por minuto en fase crucero.',
        },
        'spin.correctionDelay': {
            tipo: 'entero',
            default: DEFAULT_SPIN.correctionDelay,
            minInclusive: 0,
            maxInclusive: 5000,
            descripcion: 'Delay de corrección al final del giro (ms).',
        },
        'spin.tensionFinal': {
            tipo: 'entero',
            default: DEFAULT_SPIN.tensionFinal,
            minInclusive: 0,
            maxInclusive: 50,
            descripcion: 'Últimos N clicks donde aplica tensión psicológica.',
        },
        'spin.phases.accel': {
            tipo: 'numero',
            default: DEFAULT_SPIN.phases.accel,
            minInclusive: 0,
            maxInclusive: 1,
            descripcion: 'Fracción de tiempo en fase aceleración (0..1).',
        },
        'spin.phases.cruise': {
            tipo: 'numero',
            default: DEFAULT_SPIN.phases.cruise,
            minInclusive: 0,
            maxInclusive: 1,
            descripcion: 'Fracción de tiempo en fase crucero (0..1).',
        },
        'spin.phases.decel': {
            tipo: 'numero',
            default: DEFAULT_SPIN.phases.decel,
            minInclusive: 0,
            maxInclusive: 1,
            descripcion: 'Fracción de tiempo en fase desaceleración (0..1).',
        },

        // ─── Effects ────────────────────────────────────────────────────
        'effects.start': {
            tipo: 'string',
            default: DEFAULT_EFFECTS.start,
            maxLength: 64,
            descripcion: 'Efecto visual al iniciar el giro.',
        },
        'effects.end': {
            tipo: 'string',
            default: DEFAULT_EFFECTS.end,
            maxLength: 64,
            descripcion: 'Efecto visual al terminar el giro.',
        },

        // ─── Idle ───────────────────────────────────────────────────────
        'idle.mode': {
            tipo: 'string',
            default: DEFAULT_IDLE.mode,
            maxLength: 32,
            descripcion: 'Modo de comportamiento en espera (all, none, dim).',
        },
        'idle.timeout': {
            tipo: 'entero',
            default: DEFAULT_IDLE.timeout,
            minInclusive: 0,
            maxInclusive: 600,
            descripcion: 'Segundos antes de entrar en modo idle.',
        },

        // ─── Apuestas habilitadas ───────────────────────────────────────
        // Cada apuesta es un booleano con `aplicaSi` que delega al SSOT
        // `roulette-numbers-order-config.js > availableBets`. Si una apuesta
        // NO aplica para el tipoRuleta actual, el contrato la ignora:
        //   - No se hidrata default.
        //   - No se valida.
        //   - No se persiste si el modal la escribiera por error.
        //
        // Las apuestas no listadas acá (color, paridad, mitad, tercios,
        // columnas, bloques) están en TODOS los tipos y no necesitan aplicaSi.
        'apuestas.aciertoDirecto': {
            tipo: 'booleano',
            default: true,
            descripcion: 'Apuesta a un solo número. Siempre disponible.',
        },
        'apuestas.duo': {
            tipo: 'booleano',
            default: true,
            descripcion: 'Apuesta a 2 números adyacentes (incluye combinaciones del sector superior).',
        },
        'apuestas.linea': {
            tipo: 'booleano',
            default: true,
            aplicaSi: _aplicaSiTipoRuletaSoporta('linea'),
            descripcion: 'Apuesta a 3 números (requiere ≥3 columnas en el tapete).',
        },
        'apuestas.cuadra': {
            tipo: 'booleano',
            default: true,
            aplicaSi: _aplicaSiTipoRuletaSoporta('cuadra'),
            descripcion: 'Apuesta a 4 números adyacentes (requiere maxNum divisible por 4).',
        },
        'apuestas.dobleLinea': {
            tipo: 'booleano',
            default: true,
            aplicaSi: _aplicaSiTipoRuletaSoporta('dobleLinea'),
            descripcion: 'Apuesta a 6 números (requiere ≥3 columnas).',
        },
        'apuestas.espejos': {
            tipo: 'booleano',
            default: true,
            aplicaSi: _aplicaSiTipoRuletaSoporta('espejos'),
            descripcion: 'Apuestas espejo (solo en ruedas de 36 números).',
        },
        'apuestas.gemelos': {
            tipo: 'booleano',
            default: true,
            aplicaSi: _aplicaSiTipoRuletaSoporta('gemelos'),
            descripcion: 'Apuestas gemelas (solo en ruedas de 36 números).',
        },

        // ─── Pozo (config a nivel sorteo, distinto de pozos comunes) ────
        'pozo.min_mult': {
            tipo: 'numero',
            default: DEFAULT_CONFIG.pozo.min_mult,
            minInclusive: 1,
            maxInclusive: 100,
            descripcion: 'Multiplicador mínimo del pozo del sorteo.',
        },
        'pozo.max_mult': {
            tipo: 'numero',
            default: DEFAULT_CONFIG.pozo.max_mult,
            minInclusive: 1,
            maxInclusive: 1000,
            descripcion: 'Multiplicador máximo del pozo del sorteo.',
        },

        // ─── Vigencia de tickets ────────────────────────────────────────
        ticketExpiryDays: {
            tipo: 'entero',
            default: 180,
            minInclusive: 1,
            maxInclusive: 3650,
            descripcion: 'Días de validez del ticket para cobro.',
        },

        // ─── Resolver (algoritmo matemático) ────────────────────────────
        // Sub-objeto opaco: el modal lo arma con una función collector
        // dedicada (collectResolverConfigFromForm). El contrato solo valida
        // que SEA un objeto. Los validadores específicos de cada resolver
        // viven en backend (cada resolver hace fail-fast con su shape).
        resolver: {
            tipo: 'objeto',
            default: { name: 'random', config: {} },
            descripcion: 'Configuración del algoritmo resolver. Shape varía por resolver.name.',
        },

        // ─── Lightning (multiplicadores Mega3000 / Red Door) — override por-sorteo ───
        // MISMO patrón que `resolver`: sub-objeto OPACO que se mergea sobre la receta
        // (deepMergeConfig) en obtenerLightningDeReceta. El contrato solo valida que SEA
        // objeto; el shape fino y los invariantes money-path (rtpObjetivo ∈ (0,1], mult
        // mínimo > pleno, cap ≥ 0, …) los valida `validarConfigLightning` en backend con
        // fail-fast, igual que cada resolver valida el suyo. `default: undefined` → el
        // hidratador NO lo escribe, así un sorteo sin override no adquiere `lightning`
        // fantasma en DB y corre idéntico a hoy. `aplicaSi`: solo ruedas con lightning.
        // Pleno NO editable por-sorteo (financia los mults; cambiarlo exige recortar la
        // tabla de la receta — validarConfigLightning lo cruza y explota si desincroniza).
        lightning: {
            tipo: 'objeto',
            default: undefined,
            aplicaSi: _aplicaSiRuedaTieneLightning,
            descripcion:
                'Override de la config lightning para ESTE sorteo (rtpObjetivo, capPorApuesta, ' +
                'tiers, bonus.ruedaBonus, activo, …). Se mergea sobre la receta. Ausente = usa la receta.',
        },

        // ─── Branding (assets visuales) ─────────────────────────────────
        // Sub-objeto opaco. Modal lo edita por upload independiente,
        // save() lo preserva tal como vino del snapshot.
        assets: {
            tipo: 'objeto',
            default: null,
            descripcion: 'Assets de branding (logos, fondos). Editables por upload independiente.',
        },

        // ─── Presentación del cero (UI opcional) ────────────────────────
        // Opciones derivadas del SSOT `ZERO_DISPLAY_MODES` (en
        // `roulette-numbers-order-config.js`). Cero duplicación — el SSOT
        // canónico decide qué modos existen, el contrato los espeja.
        zero_display_mode: {
            tipo: 'enum',
            opciones: Object.values(ZERO_DISPLAY_MODES),
            default: null,
            descripcion: 'Cómo se presenta el cero en el tapete. null = default POR_NUMERO.',
        },
        zero_display_labels: {
            tipo: 'array',
            default: null,
            descripcion: 'Labels custom del cero ({top, bot}[]). Solo si mode=POR_TEXTO.',
        },
    },
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { CONTRATO_SORTEO_LOTOWHEEL };
}

export { CONTRATO_SORTEO_LOTOWHEEL };
